/**
 * ld-compare — compare-products foundation JS.
 *
 * Source of truth: project/directives/compare.md (Pattern 44).
 *
 * Owns the storage protocol + custom-element registry for the compare
 * feature:
 *
 *   localStorage.ld-compare         JSON array of selected products
 *   sessionStorage.ld-compare-mode  "on" | "off" (PLP toolbar state)
 *
 *   document custom events:
 *     'ld-compare-updated'      detail = { items: <localStorage array> }
 *     'ld-compare-mode-changed' detail = { mode: 'on' | 'off' }
 *
 *   window 'storage' event re-fires the matching custom event for
 *   cross-tab synchronisation (closes the gap in the reference theme).
 *
 * Custom elements registered:
 *   <ld-compare-toolbar-toggle>  PLP toolbar pill switch
 *   <ld-compare-card-toggle>     per-card checkbox row
 *   <ld-compare-tray>            fixed-bottom drawer
 *   <ld-compare-grid>            compare-page side-by-side renderer
 *   <ld-compare-cell>            per-cell hydration target inside the grid
 *
 * Authoring inline localStorage writes / event names elsewhere violates
 * the directive — call into the custom elements' APIs only.
 */

(function () {
  'use strict';

  const STORAGE_KEY = 'ld-compare';
  const MODE_KEY = 'ld-compare-mode';
  const MAX_COMPARE = 3;
  const COMPARE_PAGE_URL = '/pages/compare-products';

  /* ────────────────────────────────────────────────────────────────────
   * State helpers
   * ──────────────────────────────────────────────────────────────────── */

  function readSelection() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      return parsed.slice(0, MAX_COMPARE);
    } catch (_) {
      return [];
    }
  }

  function writeSelection(items) {
    const trimmed = (items || []).slice(0, MAX_COMPARE);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(trimmed));
    document.dispatchEvent(
      new CustomEvent('ld-compare-updated', { detail: { items: trimmed } })
    );
  }

  function readMode() {
    try {
      return sessionStorage.getItem(MODE_KEY) === 'on' ? 'on' : 'off';
    } catch (_) {
      return 'off';
    }
  }

  function writeMode(mode) {
    const value = mode === 'on' ? 'on' : 'off';
    try {
      sessionStorage.setItem(MODE_KEY, value);
    } catch (_) {
      /* fail open */
    }
    document.body.dataset.compareMode = value;
    document.dispatchEvent(
      new CustomEvent('ld-compare-mode-changed', { detail: { mode: value } })
    );
  }

  function applyModeToBody() {
    document.body.dataset.compareMode = readMode();
  }

  /* ────────────────────────────────────────────────────────────────────
   * <ld-compare-toolbar-toggle>
   * ──────────────────────────────────────────────────────────────────── */

  class LdCompareToolbarToggle extends HTMLElement {
    constructor() {
      super();
      this._onClick = this._onClick.bind(this);
      this._onKey = this._onKey.bind(this);
      this._onModeChanged = this._onModeChanged.bind(this);
    }
    connectedCallback() {
      this.addEventListener('click', this._onClick);
      this.addEventListener('keydown', this._onKey);
      document.addEventListener('ld-compare-mode-changed', this._onModeChanged);
      this._reflectMode(readMode());
    }
    disconnectedCallback() {
      this.removeEventListener('click', this._onClick);
      this.removeEventListener('keydown', this._onKey);
      document.removeEventListener('ld-compare-mode-changed', this._onModeChanged);
    }
    _onClick() {
      const next = readMode() === 'on' ? 'off' : 'on';
      writeMode(next);
    }
    _onKey(e) {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this._onClick();
      }
    }
    _onModeChanged(e) {
      this._reflectMode(e.detail && e.detail.mode);
    }
    _reflectMode(mode) {
      const on = mode === 'on';
      this.setAttribute('aria-checked', on ? 'true' : 'false');
      this.dataset.mode = on ? 'on' : 'off';
    }
  }

  /* ────────────────────────────────────────────────────────────────────
   * <ld-compare-card-toggle>
   * ──────────────────────────────────────────────────────────────────── */

  class LdCompareCardToggle extends HTMLElement {
    constructor() {
      super();
      this._onChange = this._onChange.bind(this);
      this._onUpdated = this._onUpdated.bind(this);
    }
    connectedCallback() {
      this.input = this.querySelector('input[type="checkbox"]');
      if (!this.input) return;
      this.input.addEventListener('change', this._onChange);
      document.addEventListener('ld-compare-updated', this._onUpdated);
      this._sync(readSelection());
    }
    disconnectedCallback() {
      if (this.input) this.input.removeEventListener('change', this._onChange);
      document.removeEventListener('ld-compare-updated', this._onUpdated);
    }
    _payload() {
      const handle = this.dataset.productHandle;
      if (!handle) return null;
      const raw = this.dataset.productPayload;
      if (!raw) return { handle: handle };
      try {
        return JSON.parse(raw);
      } catch (_) {
        return { handle: handle };
      }
    }
    _onChange() {
      const items = readSelection();
      const payload = this._payload();
      if (!payload || !payload.handle) return;
      const idx = items.findIndex(function (i) { return i.handle === payload.handle; });
      if (this.input.checked) {
        if (idx === -1) {
          if (items.length >= MAX_COMPARE) {
            this.input.checked = false;
            return;
          }
          items.push(payload);
        }
      } else if (idx !== -1) {
        items.splice(idx, 1);
      }
      writeSelection(items);
    }
    _onUpdated(e) {
      this._sync(e.detail && e.detail.items);
    }
    _sync(items) {
      if (!this.input) return;
      const handle = this.dataset.productHandle;
      const list = items || [];
      const isSelected = list.some(function (i) { return i.handle === handle; });
      this.input.checked = isSelected;
      if (list.length >= MAX_COMPARE && !isSelected) {
        this.input.disabled = true;
        this.dataset.disabled = 'true';
      } else {
        this.input.disabled = false;
        delete this.dataset.disabled;
      }
    }
  }

  /* ────────────────────────────────────────────────────────────────────
   * <ld-compare-tray>
   * ──────────────────────────────────────────────────────────────────── */

  class LdCompareTray extends HTMLElement {
    constructor() {
      super();
      this._onUpdated = this._onUpdated.bind(this);
      this._onModeChanged = this._onModeChanged.bind(this);
      this._onClick = this._onClick.bind(this);
    }
    connectedCallback() {
      this.countEl = this.querySelector('.ld-compare-tray__count');
      this.chipSlots = Array.from(this.querySelectorAll('.ld-compare-tray__chip-slot'));
      this.cta = this.querySelector('.ld-compare-tray__cta');
      this.ctaLabel = this.querySelector('.ld-compare-tray__cta-label');
      this.clearBtn = this.querySelector('.ld-compare-tray__clear');
      this.closeBtn = this.querySelector('.ld-compare-tray__close');
      document.addEventListener('ld-compare-updated', this._onUpdated);
      document.addEventListener('ld-compare-mode-changed', this._onModeChanged);
      this.addEventListener('click', this._onClick);
      this._render(readSelection(), readMode());
    }
    disconnectedCallback() {
      document.removeEventListener('ld-compare-updated', this._onUpdated);
      document.removeEventListener('ld-compare-mode-changed', this._onModeChanged);
      this.removeEventListener('click', this._onClick);
    }
    _onUpdated(e) {
      this._render(e.detail && e.detail.items, readMode());
    }
    _onModeChanged(e) {
      this._render(readSelection(), (e.detail && e.detail.mode) || 'off');
    }
    _onClick(e) {
      const chip = e.target.closest('.ld-compare-tray__chip');
      if (chip) {
        const handle = chip.dataset.productHandle;
        if (handle) {
          const items = readSelection().filter(function (i) { return i.handle !== handle; });
          writeSelection(items);
          return;
        }
      }
      if (this.clearBtn && this.clearBtn.contains(e.target)) {
        writeSelection([]);
        return;
      }
      if (this.closeBtn && this.closeBtn.contains(e.target)) {
        writeMode('off');
        return;
      }
      if (this.cta && this.cta.contains(e.target)) {
        if (this.cta.dataset.ctaDisabled === 'true') {
          e.preventDefault();
        }
      }
    }
    _render(itemsRaw, mode) {
      const items = itemsRaw || [];
      const count = items.length;
      let state = 'empty';
      if (count >= MAX_COMPARE) state = 'max';
      else if (count >= 2) state = 'ready';
      else if (count > 0) state = 'partial';
      this.dataset.state = state;
      this.dataset.count = String(count);

      const ctaDisabled = state === 'empty' || state === 'partial';
      if (this.cta) {
        this.cta.dataset.ctaDisabled = ctaDisabled ? 'true' : 'false';
        this.cta.setAttribute('aria-disabled', ctaDisabled ? 'true' : 'false');
        if (ctaDisabled) {
          this.cta.removeAttribute('href');
        } else {
          this.cta.setAttribute('href', COMPARE_PAGE_URL);
        }
      }
      if (this.ctaLabel) {
        if (ctaDisabled) {
          const partial = this.ctaLabel.dataset.partial || 'SELECT {{ needed }} TO COMPARE';
          // Display the configured max minus current count so the number
          // mirrors the cap the merchant has authorised (3 by default).
          const needed = Math.max(MAX_COMPARE - count, 1);
          this.ctaLabel.textContent = partial.replace(/{{\s*needed\s*}}/g, String(needed));
        } else {
          this.ctaLabel.textContent = this.ctaLabel.dataset.ready || 'COMPARE PRODUCTS';
        }
      }
      if (this.clearBtn) {
        this.clearBtn.hidden = count < 2;
      }
      if (this.countEl) {
        const empty = this.countEl.dataset.empty || '0 Selected';
        if (state === 'max') {
          this.countEl.textContent = (this.countEl.dataset.max || 'Maximum');
        } else if (count === 0) {
          this.countEl.textContent = empty;
        } else {
          this.countEl.textContent = String(count) + ' Selected';
        }
      }
      this.chipSlots.forEach(function (slot, idx) {
        const item = items[idx];
        const chip = slot.querySelector('.ld-compare-tray__chip');
        if (!chip) return;
        const img = chip.querySelector('.ld-compare-tray__chip-image');
        const aria = chip.querySelector('.ld-compare-tray__chip-aria');
        if (!item) {
          // Empty placeholder — chip stays in flow per Figma's per-state
          // mock (3 placeholders visible at 0/1/2 selected). CSS keys off
          // data-empty="true" to hide the <img> + remove-X so the slot
          // reads as a plain gray box, not a broken-image button.
          slot.hidden = false;
          chip.hidden = false;
          chip.dataset.empty = 'true';
          chip.dataset.productHandle = '';
          chip.setAttribute('tabindex', '-1');
          chip.setAttribute('aria-hidden', 'true');
          if (img) {
            img.removeAttribute('src');
            img.alt = '';
          }
          if (aria) aria.textContent = '';
          return;
        }
        slot.hidden = false;
        chip.hidden = false;
        chip.dataset.empty = 'false';
        chip.dataset.productHandle = item.handle || '';
        chip.removeAttribute('tabindex');
        chip.removeAttribute('aria-hidden');
        if (img) {
          if (item.image_url) {
            img.src = item.image_url;
          } else {
            img.removeAttribute('src');
          }
          img.alt = item.title || '';
        }
        if (aria) {
          aria.textContent = 'Remove ' + (item.title || '') + ' from comparison';
        }
      });

      const visible = mode === 'on';
      if (visible) {
        this.removeAttribute('hidden');
      } else {
        this.setAttribute('hidden', '');
      }
    }
  }

  /* ────────────────────────────────────────────────────────────────────
   * <ld-compare-grid>
   * ──────────────────────────────────────────────────────────────────── */

  class LdCompareGrid extends HTMLElement {
    constructor() {
      super();
      this._onUpdated = this._onUpdated.bind(this);
    }
    connectedCallback() {
      this.tableEl = this.querySelector('.ld-compare-grid__table');
      this.emptyEl = this.querySelector('.ld-compare-grid__empty');
      document.addEventListener('ld-compare-updated', this._onUpdated);
      this._render(readSelection());
    }
    disconnectedCallback() {
      document.removeEventListener('ld-compare-updated', this._onUpdated);
    }
    _onUpdated(e) {
      this._render(e.detail && e.detail.items);
    }
    async _render(itemsRaw) {
      const items = (itemsRaw || []).slice(0, MAX_COMPARE);
      const count = items.length;
      // Adaptive columns — only show as many product columns as selections.
      this.style.setProperty('--ld-compare-cols', String(count || 1));
      if (count === 0) {
        // No selection — clear loading flag so empty-state is visible.
        this.dataset.loading = 'false';
        if (this.emptyEl) this.emptyEl.hidden = false;
        if (this.tableEl) this.tableEl.hidden = true;
        return;
      }

      // Bump a render token so a stale fetch (from a prior selection) can't
      // overwrite the latest render after the user changes the list.
      const token = (this._renderToken = (this._renderToken || 0) + 1);
      this.dataset.loading = 'true';

      const fragments = await Promise.all(
        items.map(function (item) { return fetchRowPayload(item.handle); })
      );

      // Bail if a newer render started while we were fetching.
      if (token !== this._renderToken) return;
      this.dataset.loading = 'false';
      if (this.emptyEl) this.emptyEl.hidden = true;
      if (this.tableEl) this.tableEl.hidden = false;

      // Hydrate product cells (top row). Show only `count` cells; hide the
      // rest. The product card markup is rendered server-side under the
      // `data-cell="card"` element; we graft the whole thing into the cell.
      const productCells = Array.from(this.querySelectorAll('.ld-compare-grid__product'));
      productCells.forEach(function (cell, idx) {
        if (idx >= count) {
          cell.hidden = true;
          cell.innerHTML = '';
          return;
        }
        cell.hidden = false;
        const item = items[idx];
        const frag = fragments[idx];
        if (!item || !frag) {
          cell.innerHTML = '<div class="ld-compare-grid__product-empty">—</div>';
          return;
        }
        const cardSrc = frag.querySelector('[data-cell="card"]');
        if (cardSrc) {
          cell.innerHTML = cardSrc.innerHTML;
        } else {
          cell.innerHTML = '';
        }
        // Append the remove button as an overlay on top-right of the card.
        const remove = document.createElement('button');
        remove.type = 'button';
        remove.className = 'ld-compare-grid__product-remove';
        remove.setAttribute('aria-label', 'Remove from comparison');
        remove.dataset.productHandle = item.handle || '';
        remove.textContent = '×';
        remove.addEventListener('click', function () {
          const next = readSelection().filter(function (i) { return i.handle !== item.handle; });
          writeSelection(next);
        });
        cell.appendChild(remove);
      });

      const cells = Array.from(this.querySelectorAll('.ld-compare-grid__cell'));
      cells.forEach(function (cell) {
        const idx = parseInt(cell.dataset.index || '0', 10);
        if (idx >= count) {
          cell.hidden = true;
          cell.innerHTML = '';
          return;
        }
        cell.hidden = false;
        const rowKind = cell.dataset.row;
        const specKey = cell.dataset.specKey;
        const frag = fragments[idx];
        if (!frag) {
          cell.innerHTML = '<span class="ld-compare-grid__cell-empty">—</span>';
          return;
        }
        let source = null;
        if (rowKind === 'spec' && specKey) {
          source = frag.querySelector('[data-cell="spec"][data-spec-key="' + specKey + '"]');
        } else if (rowKind) {
          source = frag.querySelector('[data-cell="' + rowKind + '"]');
        }
        if (source && source.innerHTML.trim() !== '') {
          cell.innerHTML = source.innerHTML;
        } else {
          cell.innerHTML = '<span class="ld-compare-grid__cell-empty">—</span>';
        }
      });
    }
  }

  async function fetchRowPayload(handle) {
    if (!handle) return null;
    try {
      const res = await fetch('/products/' + encodeURIComponent(handle) + '?view=compare-row', {
        credentials: 'same-origin',
      });
      if (!res.ok) return null;
      const html = await res.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      return doc.querySelector('ld-compare-row-payload');
    } catch (_) {
      return null;
    }
  }

  /* ────────────────────────────────────────────────────────────────────
   * Cross-tab sync via window 'storage'
   * ──────────────────────────────────────────────────────────────────── */

  function onStorage(e) {
    if (!e) return;
    if (e.key === STORAGE_KEY) {
      document.dispatchEvent(
        new CustomEvent('ld-compare-updated', { detail: { items: readSelection() } })
      );
    } else if (e.key === MODE_KEY) {
      document.body.dataset.compareMode = readMode();
      document.dispatchEvent(
        new CustomEvent('ld-compare-mode-changed', { detail: { mode: readMode() } })
      );
    }
  }

  /* ────────────────────────────────────────────────────────────────────
   * Bootstrap
   * ──────────────────────────────────────────────────────────────────── */

  function bootstrap() {
    // Seed empty selection if missing — keeps reads idempotent.
    try {
      if (localStorage.getItem(STORAGE_KEY) === null) {
        localStorage.setItem(STORAGE_KEY, '[]');
      }
    } catch (_) { /* fail open */ }
    applyModeToBody();
    window.addEventListener('storage', onStorage);
  }

  if (!customElements.get('ld-compare-toolbar-toggle')) {
    customElements.define('ld-compare-toolbar-toggle', LdCompareToolbarToggle);
  }
  if (!customElements.get('ld-compare-card-toggle')) {
    customElements.define('ld-compare-card-toggle', LdCompareCardToggle);
  }
  if (!customElements.get('ld-compare-tray')) {
    customElements.define('ld-compare-tray', LdCompareTray);
  }
  if (!customElements.get('ld-compare-grid')) {
    customElements.define('ld-compare-grid', LdCompareGrid);
  }
  // <ld-compare-cell> + <ld-compare-row-payload> are passive containers — no
  // JS lifecycle. Defining them silences unknown-element warnings.
  if (!customElements.get('ld-compare-cell')) {
    customElements.define('ld-compare-cell', class extends HTMLElement {});
  }
  if (!customElements.get('ld-compare-row-payload')) {
    customElements.define('ld-compare-row-payload', class extends HTMLElement {});
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrap);
  } else {
    bootstrap();
  }
})();
